import React, { useEffect, useState, useRef } from 'react';
import { BadgeCheck } from 'lucide-react';
import { api } from '../services/api';

/**
 * Compact "Verified broker" pill that quietly appears when the given email
 * matches a known FCA-registered broker in our registry. Renders nothing if
 * unverified - the trust signal is purely positive.
 *
 * Props:
 *   email   - the recipient email string to check (debounced internally)
 *   size    - 'sm' (default) | 'md'
 */
export default function BrokerBadge({ email, size = 'sm' }) {
  const [data, setData] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!email || !email.includes('@')) { setData(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.verifyBroker(email.trim());
        setData(r.data?.verified ? r.data : null);
      } catch {
        setData(null);
      }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [email]);

  if (!data || !data.verified) return null;

  const isLarge = size === 'md';
  return (
    <span
      data-testid="broker-badge"
      className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 ${
        isLarge ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[10px]'
      } uppercase tracking-wider font-medium`}
      title={`Verified by Assagart${data.fca_number ? ` · FCA #${data.fca_number}` : ''}`}
    >
      <BadgeCheck size={isLarge ? 13 : 11} className="text-emerald-400" />
      <span>Verified</span>
      <span className="text-emerald-300/80 normal-case tracking-normal">·</span>
      <span className="text-white normal-case tracking-normal">{data.firm_name}</span>
      {data.fca_number && (
        <span className="text-emerald-300/60 normal-case tracking-normal">FCA #{data.fca_number}</span>
      )}
    </span>
  );
}
